import type {CategoriesMap, CategoryKey} from './types'

export const CAT_ORDER: CategoryKey[] = [
  'ai-tools',
  'design',
  'comms',
  'dev-tools',
  'cloud',
  'marketing',
  'crm',
  'ai-dev',
]

export const CATEGORIES: CategoriesMap = {
  'ai-tools': {
    label: 'AI Tools',
    theme: {bg: '#002414', accent: '#B4FF44', motif: 'ai', altLabel: 'Artificial intelligence'},
    headline: ['What European companies', 'actually pay for AI.'],
    intro:
      'OpenAI still leads on raw adoption, but Anthropic now captures the majority of AI spend and costs far more per company. AI is the 3rd-largest software category in Europe, ahead of Comms & Collab.',
    stats: {
      companies: {val: '#3', label: 'Rank by spend share', sub: 'of 19 SaaS categories'},
      adoption: {val: '52.3%', label: 'AI adoption rate', sub: '+72% year-over-year growth'},
      spend: {val: '€7.1M', label: 'AI spend (Jan–May 2026)'},
      yoySpend: {val: '+309%', label: 'YoY spend growth', tone: 'pos'},
      vendors: {val: '23', label: 'AI vendors tracked'},
    },
    aiSurge: true,
    leaderboard: {
      intro:
        'Average monthly spend per company, by team size. Not list prices, what European businesses actually pay in 2026.',
      rows: [
        ['#10A37F', 'OpenAI', '€486', '€368', '€602'],
        ['#D97757', 'Anthropic', '€450', '€779', '€1,604'],
        ['#0D0D0D', 'ElevenLabs', '€35', '€109', '€62'],
        ['#20808D', 'Perplexity', '€102', '€77', '€119'],
        ['#1A1A1A', 'Midjourney', '€33', '€42', '€59'],
        ['#0F2B46', 'DeepL', '€39', '€40', '€61'],
        ['#7C3AED', 'Higgsfield', '€134', '€110', 'n/a'],
        ['#2B2B2B', 'HeyGen', '€63', '€95', 'n/a'],
        ['#5C4A87', 'Wispr', '€35', '€74', 'n/a'],
        ['#FF6B35', 'OpenRouter', '€125', '€195', 'n/a'],
      ],
    },
    switching: {
      intro: 'Top destinations when companies switched AI tools across the full Jan 2024 – May 2026 window.',
      rows: [
        {rank: 1, name: 'Anthropic', switchIns: 301, from: 20},
        {rank: 2, name: 'OpenAI', switchIns: 64, from: 19},
        {rank: 3, name: 'ElevenLabs', switchIns: 64, from: 20},
        {rank: 4, name: 'Higgsfield', switchIns: 48, from: 21},
        {rank: 5, name: 'Wispr', switchIns: 47, from: 21},
      ],
    },
    headToHead: {
      title: 'OpenAI vs Anthropic',
      a: {name: 'OpenAI', companies: '73.2%', spendShare: '38.5%', spend: '€453', spendLabel: 'Avg monthly spend'},
      b: {name: 'Anthropic', companies: '68.1%', spendShare: '52.8%', spend: '€745', spendLabel: 'Avg monthly spend'},
      flow: [
        {type: 'both', text: '<strong>Still use both</strong>', pct: 67},
        {type: 'win', text: '<strong>Kept Anthropic only</strong>', pct: 22},
        {type: 'win', text: '<strong>Kept OpenAI only</strong>', pct: 11},
      ],
      footer:
        "<strong>Anthropic now captures 52.8% of all AI Tools spend</strong> despite slightly lower adoption than OpenAI (68.1% vs 73.2%). The spending gap is widening, Anthropic averages €745/mo vs OpenAI's €453. Co-adoption climbed from 62% to 67%: most companies use both rather than choosing one.",
    },
    distribution: {
      intro:
        'Averages hide everything. Below: the typical company, the range where 80% of customers fall, and the top end of what anyone pays, for both market leaders.',
      tools: [
        {
          name: 'OpenAI',
          color: '#10A37F',
          rows: [
            {
              size: 'Small · 1–19',
              p10: 17,
              median: 46,
              p90: 415,
              max: 116551,
              takeaway:
                'Half pay under <strong>€46/mo</strong>. The biggest spenders pay <strong>€117K/mo</strong> on the API alone.',
            },
            {
              size: 'Mid · 20–99',
              p10: 17,
              median: 101,
              p90: 657,
              max: 18345,
              takeaway: 'Typical: <strong>€101/mo</strong>. Heavy API users push past <strong>€650/mo</strong>.',
            },
            {
              size: 'Large · 100+',
              p10: 19,
              median: 145,
              p90: 1583,
              max: 10468,
              takeaway:
                'Even at 100+ employees, half pay under <strong>€145</strong>. Top 10% are <strong>€1.6K+</strong>.',
            },
          ],
        },
        {
          name: 'Anthropic',
          color: '#D97757',
          rows: [
            {
              size: 'Small · 1–19',
              p10: 18,
              median: 156,
              p90: 765,
              max: 17349,
              takeaway: "Median <strong>€156</strong>, 3.4× OpenAI's at this size.",
            },
            {
              size: 'Mid · 20–99',
              p10: 22,
              median: 229,
              p90: 1744,
              max: 39281,
              takeaway:
                "Typical: <strong>€229/mo</strong>, 2.3× OpenAI's median. The shift from Pro seats to API is dramatic.",
            },
            {
              size: 'Large · 100+',
              p10: 21,
              median: 298,
              p90: 3150,
              max: 44423,
              takeaway: 'Half pay under <strong>€298</strong>. Top spenders push past <strong>€44K/mo</strong>.',
            },
          ],
        },
      ],
      insight:
        "<strong>Anthropic's median is now 2–3× OpenAI's at every size</strong> (€156 vs €46 small, €229 vs €101 mid, €298 vs €145 large). Anthropic has shifted from individual Pro subscriptions to serious enterprise API usage, and they're charging for it.",
    },
    spendBySize: [
      {
        range: 'Small · 1–19 employees',
        spend: '€703',
        subLabel: 'AI buyers at this size run 2–3 tools on average. OpenAI + Anthropic is the default pair.',
      },
      {
        range: 'Mid · 20–99 employees',
        spend: '€876',
        subLabel: '25% more than small. AI starts to multiply across teams.',
      },
      {
        range: 'Large · 100+ employees',
        spend: '€1,534',
        subLabel: '2.2× the small-company AI bill. API costs and Team rollouts dominate.',
      },
    ],
  },

  design: {
    label: 'Design',
    theme: {bg: '#1F1018', accent: '#FF7A59', motif: 'design', altLabel: 'Creative tools'},
    headline: ['Adobe has 2× the adoption.', 'Figma costs more.'],
    intro:
      'Adobe leads on adoption and now captures 50.1% of Design spend. Figma sits higher per company at every size. Co-adoption is growing, 46% of companies run both.',
    stats: {
      companies: {val: '#6', label: 'Rank by spend share', sub: 'of 19 SaaS categories'},
      adoption: {val: '39.7%', label: 'Adoption across dataset', sub: '+37.2% year-over-year growth'},
      spend: {val: '€1.9M', label: 'Spend (Jan–May 2026)'},
      yoySpend: {val: '+36%', label: 'YoY spend growth', tone: 'pos'},
      vendors: {val: '17', label: 'Design vendors tracked'},
    },
    leaderboard: {
      intro:
        'Adobe dominates by company count. Figma quietly costs more per customer at every size. Framer is the surprise top-10 entrant.',
      rows: [
        ['#EB1000', 'Adobe', '€126', '€239', '€532'],
        ['#A259FF', 'Figma', '€179', '€315', '€967'],
        ['#00C4CC', 'Canva', '€115', '€67', '€74'],
        ['#000000', 'CapCut', '€34', '€36', 'n/a'],
        ['#82B541', 'Envato', '€29', '€28', 'n/a'],
        ['#13C5BD', 'Artlist', '€67', '€137', 'n/a'],
        ['#F36D43', 'Lucid Software', 'n/a', 'n/a', 'n/a'],
        ['#101111', 'Mobbin', 'n/a', '€76', 'n/a'],
        ['#0696D7', 'Autodesk', 'n/a', 'n/a', 'n/a'],
        ['#0055FF', 'Framer', '€115', 'n/a', 'n/a'],
      ],
    },
    switching: {
      intro:
        'Top destinations when companies switched design tools. Adobe still leads on switch-ins, but Canva and CapCut keep pulling from the bottom.',
      rows: [
        {rank: 1, name: 'Adobe', switchIns: 46, from: 12},
        {rank: 2, name: 'Figma', switchIns: 44, from: 15},
        {rank: 3, name: 'CapCut', switchIns: 38, from: 15},
        {rank: 4, name: 'Canva', switchIns: 29, from: 13},
        {rank: 5, name: 'Artlist', switchIns: 18, from: 10},
      ],
    },
    headToHead: {
      title: 'Adobe vs Figma',
      a: {name: 'Adobe', companies: '68.3%', spendShare: '50.1%', spend: '€242', spendLabel: 'Avg monthly spend'},
      b: {name: 'Figma', companies: '35.8%', spendShare: '30.2%', spend: '€359', spendLabel: 'Avg monthly spend'},
      flow: [
        {type: 'both', text: '<strong>Still use both</strong>', pct: 46},
        {type: 'win', text: '<strong>Dropped Figma for Adobe</strong>', pct: 29},
        {type: 'win', text: '<strong>Dropped Adobe for Figma</strong>', pct: 25},
      ],
      footer:
        "<strong>Co-adoption is growing, 46% now use both</strong> (up from 39%). Adobe leads on adoption (68.3% vs 35.8%) and captures 50.1% of Design spend vs Figma's 30.2%. Figma costs 1.5× more per company (€359 vs €242), but fewer companies switch to Figma (25%) than to Adobe (29%).",
    },
    distribution: {
      intro: 'Figma sits slightly higher per company at every size, even though Adobe has 2× the adoption.',
      tools: [
        {
          name: 'Adobe',
          color: '#EB1000',
          rows: [
            {
              size: 'Small · 1–19',
              p10: 20,
              median: 62,
              p90: 330,
              max: 1631,
              takeaway: 'Half pay under <strong>€62/mo</strong>. Tight range, predictable per-seat pricing.',
            },
            {
              size: 'Mid · 20–99',
              p10: 25,
              median: 108,
              p90: 561,
              max: 2478,
              takeaway: 'Typical: <strong>€108/mo</strong>.',
            },
            {
              size: 'Large · 100+',
              p10: 23,
              median: 155,
              p90: 1435,
              max: 7531,
              takeaway: 'Median <strong>€155</strong>. Top 10% pay <strong>€1.4K+</strong>.',
            },
          ],
        },
        {
          name: 'Figma',
          color: '#A259FF',
          rows: [
            {
              size: 'Small · 1–19',
              p10: 20,
              median: 70,
              p90: 327,
              max: 8383,
              takeaway: 'Median <strong>€70</strong>, slightly higher than Adobe per company.',
            },
            {
              size: 'Mid · 20–99',
              p10: 25,
              median: 139,
              p90: 607,
              max: 5980,
              takeaway: 'Typical: <strong>€139/mo</strong>.',
            },
            {
              size: 'Large · 100+',
              p10: 25,
              median: 159,
              p90: 965,
              max: 30944,
              takeaway: 'Half pay under <strong>€159</strong>. Heavy users push <strong>€30K+</strong>.',
            },
          ],
        },
      ],
      insight:
        "<strong>Figma's median is consistently higher than Adobe's</strong> at every size. Figma is the pricier tool per company, but Adobe has twice the adoption, so it captures more total spend.",
    },
    spendBySize: [
      {
        range: 'Small · 1–19 employees',
        spend: '€203',
        subLabel: 'A typical creative pair: one Adobe seat + one Figma seat.',
      },
      {range: 'Mid · 20–99 employees', spend: '€389', subLabel: '1.9× the small spend. Creative teams form.'},
      {
        range: 'Large · 100+ employees',
        spend: '€793',
        subLabel: '3.9× small. Org-wide creative ops with Adobe + Figma seats.',
      },
    ],
  },

  comms: {
    label: 'Comms & Collab',
    theme: {bg: '#0A1426', accent: '#5BA3FF', motif: 'comms', altLabel: 'Communication'},
    headline: ['Slack wins startups.', 'Microsoft wins everyone else.'],
    intro:
      "Microsoft captures 62.5% of all Comms spend, nearly 10× Slack's share, and costs 5× what Slack costs per company.",
    stats: {
      companies: {val: '#4', label: 'Rank by spend share', sub: 'of 19 SaaS categories'},
      adoption: {val: '35.5%', label: 'Adoption across dataset', sub: '+21.7% year-over-year growth'},
      spend: {val: '€6.3M', label: 'Spend (Jan–May 2026)'},
      yoySpend: {val: '+23%', label: 'YoY spend growth', tone: 'pos'},
      vendors: {val: '14', label: 'Comms vendors tracked'},
    },
    leaderboard: {
      intro: 'Microsoft costs ~5× what Slack costs on average.',
      rows: [
        ['#0078D4', 'Microsoft', '€501', '€1,299', '€4,008'],
        ['#4A154B', 'Slack', '€185', '€442', '€162'],
        ['#101111', 'Notion', '€253', '€477', '€555'],
        ['#FFD02F', 'Miro', '€223', '€336', '€766'],
        ['#006BFF', 'Calendly', '€88', '€186', 'n/a'],
        ['#0061FF', 'Dropbox', '€118', '€295', 'n/a'],
        ['#2D8CFF', 'Zoom', '€244', '€456', '€463'],
        ['#0090FF', 'WeTransfer', '€25', '€30', 'n/a'],
        ['#1F1F1F', 'Superhuman', '€99', '€81', 'n/a'],
      ],
    },
    switching: {
      intro:
        'Top destinations when companies switched comms tools. Microsoft pulls the most, but Slack still wins fresh customers at the bottom of the market.',
      rows: [
        {rank: 1, name: 'Slack', switchIns: 47, from: 10},
        {rank: 2, name: 'Microsoft', switchIns: 45, from: 10},
        {rank: 3, name: 'Notion', switchIns: 38, from: 11},
        {rank: 4, name: 'Miro', switchIns: 34, from: 10},
        {rank: 5, name: 'Dropbox', switchIns: 27, from: 11},
      ],
    },
    headToHead: {
      title: 'Slack vs Microsoft',
      a: {name: 'Slack', companies: '30.4%', spendShare: '6.4%', spend: '€285', spendLabel: 'Avg monthly spend'},
      b: {name: 'Microsoft', companies: '47.8%', spendShare: '62.5%', spend: '€1,461', spendLabel: 'Avg monthly spend'},
      flow: [
        {type: 'win', text: '<strong>Dropped Slack for Microsoft</strong>', pct: 44},
        {type: 'both', text: '<strong>Still use both</strong>', pct: 31},
        {type: 'win', text: '<strong>Dropped Microsoft for Slack</strong>', pct: 26},
      ],
      footer:
        "<strong>Microsoft captures 62.5% of all Comms spend</strong>, nearly 10× Slack's 6.4% share. At €1,461/mo vs €285/mo the cost gap is 5×. Microsoft keeps winning the switching battle (44% vs 26%), but startups under 10 still prefer Slack.",
    },
    distribution: {
      intro:
        'Microsoft has a long tail of mega-spenders, with top enterprises reaching €252K/mo. Slack stays tightly bounded. The medians, though, tell a different story.',
      tools: [
        {
          name: 'Microsoft',
          color: '#0078D4',
          rows: [
            {
              size: 'Small · 1–19',
              p10: 12,
              median: 104,
              p90: 798,
              max: 32867,
              takeaway: 'Median <strong>€104/mo</strong>. Top small-org spenders reach <strong>€33K/mo</strong>.',
            },
            {
              size: 'Mid · 20–99',
              p10: 15,
              median: 211,
              p90: 2861,
              max: 26514,
              takeaway: 'Typical: <strong>€211/mo</strong>. Heavy users push <strong>€2.9K+</strong>.',
            },
            {
              size: 'Large · 100+',
              p10: 19,
              median: 333,
              p90: 3785,
              max: 251545,
              takeaway: 'Median <strong>€333/mo</strong>. Top enterprise spenders reach <strong>€252K/mo</strong>.',
            },
          ],
        },
        {
          name: 'Slack',
          color: '#4A154B',
          rows: [
            {
              size: 'Small · 1–19',
              p10: 17,
              median: 96,
              p90: 286,
              max: 3117,
              takeaway: 'Median <strong>€96</strong>. Tight range, even the heavy users stay bounded.',
            },
            {
              size: 'Mid · 20–99',
              p10: 10,
              median: 190,
              p90: 675,
              max: 22356,
              takeaway: 'Typical: <strong>€190/mo</strong>. Range tops out around <strong>€675</strong>.',
            },
            {
              size: 'Large · 100+',
              p10: 8,
              median: 65,
              p90: 506,
              max: 884,
              takeaway:
                '<em>Small sample.</em> Median drops to <strong>€65</strong>, large orgs that kept Slack use it lightly.',
            },
          ],
        },
      ],
      insight:
        "Microsoft's top enterprise spenders reach <strong>€252K/mo</strong>, which inflates the average. Slack's maximum (€22K) is far more contained. At the median for small companies, Slack and Microsoft are comparable, it's at Large where Microsoft pulls away.",
    },
    spendBySize: [
      {range: 'Small · 1–19 employees', spend: '€480', subLabel: 'Slack + Notion is the typical startup pairing.'},
      {range: 'Mid · 20–99 employees', spend: '€1,274', subLabel: '2.7× small. The Microsoft migration starts here.'},
      {range: 'Large · 100+ employees', spend: '€2,762', subLabel: '5.8× small. M365 per-seat licensing takes over.'},
    ],
  },

  'dev-tools': {
    label: 'Dev Tools',
    theme: {bg: '#0E1417', accent: '#FF9F45', motif: 'dev', altLabel: 'Engineering'},
    headline: ['€9M in spend, and shrinking.', 'The only top category losing money.'],
    intro:
      'GitHub, Atlassian, JetBrains still anchor the category, but spend dropped 20% YoY as companies consolidate.',
    stats: {
      companies: {val: '#2', label: 'Rank by spend share', sub: 'of 19 SaaS categories'},
      adoption: {val: '31.0%', label: 'Adoption across dataset', sub: '+39.7% year-over-year growth'},
      spend: {val: '€9.0M', label: 'Spend (Jan–May 2026)'},
      yoySpend: {val: '−20%', label: 'YoY spend growth', tone: 'neg'},
      vendors: {val: '38', label: 'Dev vendors tracked'},
    },
    leaderboard: {
      intro:
        "Atlassian commands 4–7× GitHub's spend at every size. JetBrains, Twilio and Sentry round out the heavy tier.",
      rows: [
        ['#101111', 'GitHub', '€215', '€270', '€457'],
        ['#0052CC', 'Atlassian', '€3,566', '€1,254', '€1,690'],
        ['#FF318C', 'JetBrains', '€485', '€1,408', '€1,065'],
        ['#F22F46', 'Twilio', '€252', '€511', '€1,114'],
        ['#3D2C5D', 'Sentry', '€119', '€160', '€173'],
        ['#FF6C37', 'Postman', 'n/a', '€258', 'n/a'],
        ['#0DB7ED', 'Docker', 'n/a', '€210', '€260'],
        ['#1D9BF0', 'Apify', '€52', '€103', 'n/a'],
        ['#FF6E0F', 'BrowserStack', 'n/a', '€144', 'n/a'],
        ['#13AA52', 'MongoDB', '€436', '€2,382', 'n/a'],
      ],
    },
    switching: {
      intro:
        'Where companies migrated within Dev Tools across Jan 2024 – May 2026. GitHub wins the most switch-ins overall.',
      rows: [
        {rank: 1, name: 'GitHub', switchIns: 44, from: 25},
        {rank: 2, name: 'Atlassian', switchIns: 42, from: 24},
        {rank: 3, name: 'Twilio', switchIns: 31, from: 22},
        {rank: 4, name: 'Sentry', switchIns: 30, from: 17},
        {rank: 5, name: 'Apify', switchIns: 29, from: 24},
      ],
    },
    headToHead: {
      title: 'GitHub vs Atlassian',
      a: {name: 'GitHub', companies: '47.6%', spendShare: '7.6%', spend: '€281', spendLabel: 'Avg monthly spend'},
      b: {name: 'Atlassian', companies: '44.1%', spendShare: '45.2%', spend: '€1,977', spendLabel: 'Avg monthly spend'},
      flow: [
        {type: 'both', text: '<strong>Still use both</strong>', pct: 59},
        {type: 'win', text: '<strong>Switched to Atlassian</strong>', pct: 22},
        {type: 'win', text: '<strong>Switched to GitHub</strong>', pct: 19},
      ],
      footer:
        '<strong>Atlassian captures 45.2% of all Dev Tools spend</strong> despite slightly lower adoption than GitHub (44.1% vs 47.6%). At €1,977/mo vs €281/mo, Atlassian costs 7× more per company. Co-adoption rose from 50% to 59%, complementary, not competing.',
    },
    distribution: {
      intro:
        "Atlassian's range is extraordinary. The median tells a more grounded story: Atlassian is 4–7× GitHub at every size.",
      tools: [
        {
          name: 'GitHub',
          color: '#101111',
          rows: [
            {
              size: 'Small · 1–19',
              p10: 10,
              median: 63,
              p90: 314,
              max: 5030,
              takeaway: 'Half pay under <strong>€63/mo</strong>. Heavy users near <strong>€314</strong>.',
            },
            {
              size: 'Mid · 20–99',
              p10: 16,
              median: 128,
              p90: 684,
              max: 2992,
              takeaway: 'Typical: <strong>€128/mo</strong>.',
            },
            {
              size: 'Large · 100+',
              p10: 16,
              median: 123,
              p90: 1371,
              max: 4713,
              takeaway: 'Median <strong>€123</strong>. Top 10% pay <strong>€1.4K+</strong>.',
            },
          ],
        },
        {
          name: 'Atlassian',
          color: '#0052CC',
          rows: [
            {
              size: 'Small · 1–19',
              p10: 16,
              median: 156,
              p90: 901,
              max: 324654,
              takeaway: 'Median <strong>€156</strong>.',
            },
            {
              size: 'Mid · 20–99',
              p10: 43,
              median: 482,
              p90: 2510,
              max: 40950,
              takeaway: 'Typical: <strong>€482/mo</strong>. Heavy users <strong>€2.5K+</strong>.',
            },
            {
              size: 'Large · 100+',
              p10: 61,
              median: 679,
              p90: 4383,
              max: 20554,
              takeaway: 'Median <strong>€679</strong>. Top 10% push <strong>€4.4K+</strong>.',
            },
          ],
        },
      ],
      insight:
        "<strong>Atlassian's max spend remains unique among Dev Tools.</strong> The median (€679 for Large) is more realistic. Across every size, Atlassian costs 4–7× what GitHub costs, Jira and Confluence stay sticky and expensive.",
    },
    spendBySize: [
      {
        range: 'Small · 1–19 employees',
        spend: '€1,590',
        subLabel: 'GitHub seats + Jira starter + a few cloud services.',
      },
      {
        range: 'Mid · 20–99 employees',
        spend: '€1,832',
        subLabel: 'Only 15% more than small, consolidation is keeping spend flat.',
      },
      {
        range: 'Large · 100+ employees',
        spend: '€2,554',
        subLabel: '61% above small. Procurement-negotiated annual contracts.',
      },
    ],
  },

  cloud: {
    label: 'Cloud Infrastructure',
    theme: {bg: '#03222E', accent: '#5DD6FF', motif: 'cloud', altLabel: 'Cloud infrastructure'},
    headline: ['The most expensive', 'category in SaaS.'],
    intro:
      'Google Cloud wins on breadth. AWS wins on depth, capturing 64.6% of category spend with just 25.5% adoption, the most extreme adoption-vs-spend gap in the report.',
    stats: {
      companies: {val: '#1', label: 'Rank by spend share', sub: 'of 19 SaaS categories'},
      adoption: {val: '27.6%', label: 'Adoption across dataset', sub: '+52.2% year-over-year growth'},
      spend: {val: '€9.5M', label: 'Spend (Jan–May 2026)'},
      yoySpend: {val: '+23%', label: 'YoY spend growth', tone: 'pos'},
      vendors: {val: '11', label: 'Cloud vendors tracked'},
    },
    leaderboard: {
      intro:
        'AWS spend is 5× Google Cloud per company. Hetzner remains the European value play. Supabase and Railway both entered the top 10.',
      rows: [
        ['#4285F4', 'Google Cloud', '€1,181', '€715', '€1,897'],
        ['#FF9900', 'AWS', '€2,155', '€6,500', '€7,905'],
        ['#D50C2D', 'Hetzner', '€225', '€527', '€239'],
        ['#101111', 'Vercel', '€87', '€112', 'n/a'],
        ['#F38020', 'Cloudflare', '€71', '€179', '€203'],
        ['#3ECF8E', 'Supabase', '€47', '€45', 'n/a'],
        ['#0080FF', 'DigitalOcean', '€345', '€827', 'n/a'],
        ['#864FFF', 'Railway', 'n/a', 'n/a', 'n/a'],
        ['#00C7B7', 'Netlify', 'n/a', 'n/a', 'n/a'],
        ['#123F6D', 'OVHcloud', 'n/a', 'n/a', 'n/a'],
      ],
    },
    switching: {
      intro:
        'Where companies moved in cloud across Jan 2024 – May 2026. Google Cloud pulls the most switchers; Supabase enters the top 5.',
      rows: [
        {rank: 1, name: 'Google Cloud', switchIns: 33, from: 9},
        {rank: 2, name: 'Hetzner', switchIns: 20, from: 6},
        {rank: 3, name: 'Vercel', switchIns: 17, from: 5},
        {rank: 4, name: 'Supabase', switchIns: 16, from: 6},
        {rank: 5, name: 'Cloudflare', switchIns: 15, from: 5},
      ],
    },
    headToHead: {
      title: 'Google Cloud vs AWS',
      a: {
        name: 'Google Cloud',
        companies: '51.1%',
        spendShare: '23.5%',
        spend: '€1,124',
        spendLabel: 'Avg monthly spend',
      },
      b: {name: 'AWS', companies: '25.5%', spendShare: '64.6%', spend: '€5,413', spendLabel: 'Avg monthly spend'},
      flow: [
        {type: 'both', text: '<strong>Still use both</strong>', pct: 42},
        {type: 'win', text: '<strong>Switched to Google Cloud</strong>', pct: 31},
        {type: 'win', text: '<strong>Switched to AWS</strong>', pct: 27},
      ],
      footer:
        '<strong>AWS captures 64.6% of Cloud spend with only 25.5% adoption</strong>, the most extreme adoption-vs-spend gap in any category. At €5,413/mo vs €1,124/mo, AWS costs nearly 5× more. Google Cloud leads adoption (51.1%) and wins more switchers (31% vs 27%).',
    },
    distribution: {
      intro:
        'Cloud remains the most skewed category. P10 sits near zero (free tiers). P90 is in the thousands. Median tells you what your bill really looks like.',
      tools: [
        {
          name: 'Google Cloud',
          color: '#4285F4',
          rows: [
            {
              size: 'Small · 1–19',
              p10: 1,
              median: 40,
              p90: 1667,
              max: 56169,
              takeaway:
                'Half pay under <strong>€40/mo</strong>, free tier dominates. Top orgs reach <strong>€56K/mo</strong>.',
            },
            {
              size: 'Mid · 20–99',
              p10: 1,
              median: 53,
              p90: 1124,
              max: 35889,
              takeaway: 'Median <strong>€53</strong>, free tier still dominates.',
            },
            {
              size: 'Large · 100+',
              p10: 3,
              median: 159,
              p90: 1766,
              max: 70153,
              takeaway: 'Typical: <strong>€159/mo</strong>. Heavy users push <strong>€1.8K+</strong>.',
            },
          ],
        },
        {
          name: 'AWS',
          color: '#FF9900',
          rows: [
            {
              size: 'Small · 1–19',
              p10: 6,
              median: 280,
              p90: 6110,
              max: 52106,
              takeaway: 'Median <strong>€280</strong>. Top small-org spenders reach <strong>€52K/mo</strong>.',
            },
            {
              size: 'Mid · 20–99',
              p10: 17,
              median: 838,
              p90: 17004,
              max: 110081,
              takeaway: 'Typical: <strong>€838/mo</strong>, 16× higher median than Google Cloud.',
            },
            {
              size: 'Large · 100+',
              p10: 1,
              median: 315,
              p90: 16818,
              max: 146505,
              takeaway: 'Median <strong>€315</strong>. Top spenders hit <strong>€147K/mo</strong>.',
            },
          ],
        },
      ],
      insight:
        "<strong>AWS has wildly higher spend at every level.</strong> Median AWS at Mid-size is €838, versus Google Cloud's €53. Top of range hits €147K/mo. Cloud is the most skewed category we cover: P10 near zero (free tiers), P90 in the thousands.",
    },
    spendBySize: [
      {range: 'Small · 1–19 employees', spend: '€1,044', subLabel: 'Infra scales with traffic, not headcount.'},
      {range: 'Mid · 20–99 employees', spend: '€2,681', subLabel: '2.6× small. Workloads grow as products scale.'},
      {
        range: 'Large · 100+ employees',
        spend: '€3,366',
        subLabel: '3.2× small. Cloud is the largest pure-infra cost at every size.',
      },
    ],
  },

  marketing: {
    label: 'Marketing',
    theme: {bg: '#1A1308', accent: '#FFD24A', motif: 'marketing', altLabel: 'Marketing & growth'},
    headline: ['Klaviyo outspends', 'Semrush + Ahrefs combined.'],
    intro:
      "Typeform and Sendinblue lead on adoption. Klaviyo hides in the middle of the leaderboard, but at €1,404–€1,683/mo it's the most expensive marketing tool. Semrush and Ahrefs are now exactly tied on share.",
    stats: {
      companies: {val: '#7', label: 'Rank by spend share', sub: 'of 19 SaaS categories'},
      adoption: {val: '20.2%', label: 'Adoption across dataset', sub: '+26.8% year-over-year growth'},
      spend: {val: '€1.9M', label: 'Spend (Jan–May 2026)'},
      yoySpend: {val: '+48%', label: 'YoY spend growth', tone: 'pos'},
      vendors: {val: '22', label: 'Marketing vendors tracked'},
    },
    leaderboard: {
      intro:
        'Klaviyo sits at #4 by adoption, but #1 by spend (€1,404–€1,683/mo). Mailjet and Reviews.io are the new entrants.',
      rows: [
        ['#262627', 'Typeform', '€72', '€88', '€114'],
        ['#0092FF', 'Sendinblue', '€170', '€195', 'n/a'],
        ['#FFE01B', 'Mailchimp', '€135', '€241', 'n/a'],
        ['#1E1E1E', 'Klaviyo', '€1,404', '€1,683', 'n/a'],
        ['#FF642D', 'Semrush', 'n/a', '€234', 'n/a'],
        ['#FF6C2C', 'Ahrefs', 'n/a', '€265', 'n/a'],
        ['#101111', 'Contentsquare', 'n/a', '€140', 'n/a'],
        ['#1DA1F2', 'X (Twitter)', 'n/a', 'n/a', 'n/a'],
        ['#19D5C9', 'Mailjet', 'n/a', 'n/a', 'n/a'],
        ['#7A4BFF', 'Reviews.io', 'n/a', 'n/a', 'n/a'],
      ],
    },
    switching: {
      intro: 'Top destinations when companies switched marketing tools across Jan 2024 – May 2026.',
      rows: [
        {rank: 1, name: 'Contentsquare', switchIns: 24, from: 13},
        {rank: 2, name: 'Sendinblue', switchIns: 15, from: 11},
        {rank: 3, name: 'Semrush', switchIns: 14, from: 11},
        {rank: 4, name: 'Typeform', switchIns: 12, from: 9},
        {rank: 5, name: 'Ahrefs', switchIns: 11, from: 9},
      ],
    },
    headToHead: {
      title: 'Semrush vs Ahrefs',
      a: {name: 'Semrush', companies: '10.5%', spendShare: '4.7%', spend: '€276', spendLabel: 'Avg monthly spend'},
      b: {name: 'Ahrefs', companies: '10.5%', spendShare: '4.2%', spend: '€265', spendLabel: 'Avg monthly spend'},
      flow: [
        {type: 'win', text: '<strong>Switched to Ahrefs</strong>', pct: 44},
        {type: 'win', text: '<strong>Switched to Semrush</strong>', pct: 40},
        {type: 'both', text: '<strong>Still use both</strong>', pct: 16},
      ],
      footer:
        '<strong>Semrush and Ahrefs are now exactly tied on market share at 10.5% each.</strong> Ahrefs slightly edges Semrush on switching (44% vs 40%) but Semrush leads on spend share (4.7% vs 4.2%).',
    },
    distribution: {
      intro:
        'Marketing tools remain the most predictable category in the report. Tight ranges, no multi-€100K outliers. Only Mid-size companies meet the minimum sample threshold for these two SEO tools.',
      tools: [
        {
          name: 'Semrush',
          color: '#FF642D',
          rows: [
            {
              size: 'Mid · 20–99',
              p10: 103,
              median: 196,
              p90: 409,
              max: 725,
              takeaway: 'Median <strong>€196</strong>. Tight range, no outliers.',
            },
          ],
        },
        {
          name: 'Ahrefs',
          color: '#FF6C2C',
          rows: [
            {
              size: 'Mid · 20–99',
              p10: 65,
              median: 179,
              p90: 418,
              max: 2357,
              takeaway: 'Median <strong>€179</strong>, slightly cheaper than Semrush, with heavy outliers near €2.4K.',
            },
          ],
        },
      ],
      insight:
        'Both tools have <strong>tight distributions</strong> with no extreme outliers, unlike AI, Dev Tools or Cloud. This is predictable per-seat SaaS pricing. <em>Only Mid-size companies meet the &gt;25-org threshold for reliable distribution data here.</em>',
    },
    spendBySize: [
      {range: 'Small · 1–19 employees', spend: '€586', subLabel: 'Typeform + Mailchimp + maybe Ahrefs.'},
      {
        range: 'Mid · 20–99 employees',
        spend: '€758',
        subLabel: '29% more than small. Klaviyo and Sendinblue dominate.',
      },
      {
        range: 'Large · 100+ employees',
        spend: '€759',
        subLabel: 'Flat vs mid, Klaviyo at large e-commerce holds the average up.',
      },
    ],
  },

  crm: {
    label: 'CRM & Sales',
    theme: {bg: '#170A1F', accent: '#C677F0', motif: 'crm', altLabel: 'Sales & CRM'},
    headline: ['HubSpot and Clay are', 'neck-and-neck for switchers.'],
    intro:
      'LinkedIn still leads on adoption. HubSpot dominates spend with a 55.8% share. Clay has caught HubSpot on switch-ins, and Salesforce barely registers among European SMBs.',
    stats: {
      companies: {val: '#5', label: 'Rank by spend share', sub: 'of 19 SaaS categories'},
      adoption: {val: '17.9%', label: 'Adoption across dataset', sub: '+25.5% year-over-year growth'},
      spend: {val: '€3.0M', label: 'Spend (Jan–May 2026)'},
      yoySpend: {val: '+12%', label: 'YoY spend growth', tone: 'pos'},
      vendors: {val: '10', label: 'CRM vendors tracked'},
    },
    leaderboard: {
      intro:
        "HubSpot is 3–7× pricier than Pipedrive at every size. Clay's leaderboard share is small, but it's now tied with HubSpot on switching.",
      rows: [
        ['#0A66C2', 'LinkedIn', '€372', '€544', '€1,134'],
        ['#FF7A59', 'HubSpot', '€1,203', '€2,441', 'n/a'],
        ['#22323D', 'Pipedrive', '€417', '€377', 'n/a'],
        ['#1E1E1E', 'Clay', '€465', '€484', 'n/a'],
        ['#3B82F6', 'Lemlist', '€299', '€315', 'n/a'],
        ['#C8202F', 'Zoho', '€640', '€777', 'n/a'],
        ['#34495E', 'Lusha', 'n/a', '€80', 'n/a'],
        ['#FF9900', 'Apollo.io', 'n/a', '€222', 'n/a'],
        ['#FF5132', 'PhantomBuster', 'n/a', 'n/a', 'n/a'],
        ['#00A1E0', 'Salesforce', 'n/a', 'n/a', 'n/a'],
      ],
    },
    switching: {
      intro:
        'Top destinations when companies switched CRM tools. HubSpot and Clay are neck-and-neck (34 vs 33). Salesforce: just 6 switch-ins among European SMBs across 29 months.',
      rows: [
        {rank: 1, name: 'HubSpot', switchIns: 34, from: 9},
        {rank: 2, name: 'Clay', switchIns: 33, from: 9},
        {rank: 3, name: 'LinkedIn', switchIns: 25, from: 7},
        {rank: 4, name: 'Lemlist', switchIns: 16, from: 8},
        {rank: 5, name: 'Lusha', switchIns: 14, from: 7},
      ],
    },
    headToHead: {
      title: 'HubSpot vs Pipedrive',
      a: {name: 'HubSpot', companies: '34.2%', spendShare: '55.8%', spend: '€1,785', spendLabel: 'Avg monthly spend'},
      b: {name: 'Pipedrive', companies: '17.4%', spendShare: '9.8%', spend: '€702', spendLabel: 'Avg monthly spend'},
      flow: [
        {type: 'win', text: '<strong>Switched to Pipedrive</strong>', pct: 44},
        {type: 'win', text: '<strong>Switched to HubSpot</strong>', pct: 39},
        {type: 'both', text: '<strong>Still use both</strong>', pct: 17},
      ],
      footer:
        "<strong>HubSpot dominates CRM spend with a 55.8% share</strong> despite 34.2% adoption. Pipedrive has nearly the same switching win rate (44% vs 39%), but HubSpot's spend (€1,785/mo vs €702) shows it captures the enterprise budgets.",
    },
    distribution: {
      intro:
        'HubSpot is the premium, bundled choice. Pipedrive is the focused alternative. The median spend gap is 3–6× at both sizes that meet the sample threshold.',
      tools: [
        {
          name: 'HubSpot',
          color: '#FF7A59',
          rows: [
            {
              size: 'Small · 1–19',
              p10: 42,
              median: 624,
              p90: 3373,
              max: 11897,
              takeaway: 'Half pay under <strong>€624/mo</strong>. Top 10% pay <strong>€3.4K+</strong>.',
            },
            {
              size: 'Mid · 20–99',
              p10: 119,
              median: 1317,
              p90: 5872,
              max: 21274,
              takeaway: 'Typical: <strong>€1,317/mo</strong>. Heavy users <strong>€5.9K+</strong>.',
            },
          ],
        },
        {
          name: 'Pipedrive',
          color: '#22323D',
          rows: [
            {
              size: 'Small · 1–19',
              p10: 48,
              median: 237,
              p90: 814,
              max: 4045,
              takeaway: 'Half pay under <strong>€237/mo</strong>, far cheaper than HubSpot.',
            },
            {
              size: 'Mid · 20–99',
              p10: 60,
              median: 232,
              p90: 828,
              max: 1683,
              takeaway: 'Typical: <strong>€232/mo</strong>, 6× cheaper than HubSpot at this size.',
            },
          ],
        },
      ],
      insight:
        '<strong>HubSpot median is 3–6× Pipedrive at every size.</strong> €624 vs €237 for small. €1,317 vs €232 for mid. HubSpot is the premium bundle; Pipedrive is the focused alternative for sales-only teams.',
    },
    spendBySize: [
      {
        range: 'Small · 1–19 employees',
        spend: '€920',
        subLabel: 'LinkedIn Sales Nav + HubSpot Starter is the usual stack.',
      },
      {range: 'Mid · 20–99 employees', spend: '€1,438', subLabel: '56% more than small. HubSpot Pro tiers kick in.'},
      {
        range: 'Large · 100+ employees',
        spend: '€1,999',
        subLabel: '2.2× small. Enterprise CRM rollouts and seat sprawl.',
      },
    ],
  },

  'ai-dev': {
    label: 'AI Dev Tools',
    theme: {bg: '#050507', accent: '#B4FF44', motif: 'ai-dev', altLabel: 'Coding with AI'},
    headline: ['+1,009% growth.', 'The fastest moving category.'],
    intro:
      "Cursor's average spend jumped from €130/mo to €644/mo, companies are rolling out full team seats. Cursor captures a dominant 84.5% of category spend.",
    stats: {
      companies: {val: '#10', label: 'Rank by spend share', sub: 'of 19 SaaS categories'},
      adoption: {val: '15.0%', label: 'Adoption across dataset', sub: '+121.6% year-over-year growth'},
      spend: {val: '€1.1M', label: 'Spend (Jan–May 2026)'},
      yoySpend: {val: '+1,009%', label: 'YoY spend growth', tone: 'pos'},
      vendors: {val: '4', label: 'AI dev vendors tracked'},
    },
    leaderboard: {
      intro:
        "Only four vendors with meaningful adoption. Cursor's per-company spend jumped 5× in a year. Lovable doubled its adoption. Windsurf held flat.",
      rows: [
        ['#101111', 'Cursor', '€589', '€663', '€740'],
        ['#FF6849', 'Lovable', '€102', '€202', '€168'],
        ['#F26207', 'Replit', 'n/a', '€72', 'n/a'],
        ['#0AB3F3', 'Windsurf', 'n/a', 'n/a', 'n/a'],
      ],
    },
    switching: {
      intro: 'Switching activity in AI Dev Tools across Jan 2024 – May 2026.',
      rows: [
        {rank: 1, name: 'Lovable', switchIns: 11, from: 3},
        {rank: 2, name: 'Cursor', switchIns: 11, from: 3},
        {rank: 3, name: 'Replit', switchIns: 4, from: 3},
      ],
    },
    headToHead: {
      title: 'Cursor vs Lovable',
      a: {name: 'Cursor', companies: '62.7%', spendShare: '84.5%', spend: '€644', spendLabel: 'Avg monthly spend'},
      b: {name: 'Lovable', companies: '48.8%', spendShare: '12.8%', spend: '€150', spendLabel: 'Avg monthly spend'},
      flow: [
        {type: 'both', text: '<strong>Still use both</strong>', pct: 49},
        {type: 'win', text: '<strong>Switched to Cursor</strong>', pct: 26},
        {type: 'win', text: '<strong>Switched to Lovable</strong>', pct: 26},
      ],
      footer:
        "<strong>Cursor captures a dominant 84.5% of AI Dev Tools spend</strong> despite 62.7% adoption, at €644/mo it's 4.3× more expensive than Lovable (€150). Co-adoption rose from 44% to 49%, and switching is now evenly split (26%/26%): the tools serve complementary use cases.",
    },
    distribution: {
      intro:
        "Cursor's median spend nearly doubled in a year. Lovable stays low at the median but now has a long tail, teams are building serious production apps.",
      tools: [
        {
          name: 'Cursor',
          color: '#101111',
          rows: [
            {
              size: 'Small · 1–19',
              p10: 18,
              median: 99,
              p90: 1325,
              max: 10993,
              takeaway: 'Half pay under <strong>€99</strong>. Top 10% pay <strong>€1.3K+</strong>, team rollouts.',
            },
            {
              size: 'Mid · 20–99',
              p10: 17,
              median: 96,
              p90: 1606,
              max: 9301,
              takeaway: 'Typical: <strong>€96/mo</strong>. Heavy users push <strong>€1.6K+</strong>.',
            },
            {
              size: 'Large · 100+',
              p10: 18,
              median: 207,
              p90: 1845,
              max: 7627,
              takeaway: 'Median <strong>€207</strong>, more than doubled from 2025.',
            },
          ],
        },
        {
          name: 'Lovable',
          color: '#FF6849',
          rows: [
            {
              size: 'Small · 1–19',
              p10: 21,
              median: 43,
              p90: 200,
              max: 1592,
              takeaway: 'Half pay under <strong>€43</strong>, entry-level experimentation.',
            },
            {
              size: 'Mid · 20–99',
              p10: 21,
              median: 50,
              p90: 320,
              max: 8815,
              takeaway:
                'Typical: <strong>€50/mo</strong>. Some companies run it at <strong>€8.8K/mo</strong>, serious production usage.',
            },
            {
              size: 'Large · 100+',
              p10: 25,
              median: 87,
              p90: 413,
              max: 900,
              takeaway: 'Median <strong>€87</strong>. Even at scale, usage stays light.',
            },
          ],
        },
      ],
      insight:
        "<strong>Cursor's median nearly doubled from 2025</strong> (€50 → €99 for Small, €128 → €207 for Large). <strong>Lovable's max at Mid-size hit €8,815</strong>, teams are building serious production apps on it. Cursor wins revenue; Lovable wins switching.",
    },
    spendBySize: [
      {
        range: 'Small · 1–19 employees',
        spend: '€425',
        subLabel: 'One or two seats. Still the cheapest serious AI category.',
      },
      {range: 'Mid · 20–99 employees', spend: '€567', subLabel: '33% more than small, engineering rollouts begin.'},
      {
        range: 'Large · 100+ employees',
        spend: '€576',
        subLabel: '36% above small. Org-wide adoption is just starting.',
      },
    ],
  },
}
